import { Injectable } from "@angular/core";
import { Adapter } from "@core/adapter";

// A catalogue rule opens the open-course catalogue of one connected branch to (part of) the
// organisation. No teams means everyone. Several rules can apply to the same member: as soon as one
// of them accepts automatically, the request is accepted automatically.
export interface CatalogRuleTeam {
  id: string;
  name: string;
}

export interface BranchConnectionCatalogRule {
  id: string;
  connectionId: string;
  teams: CatalogRuleTeam[];
  autoAcceptRequest: boolean;
  grantsPossible: boolean;
  themeId: string;
  active: boolean;
  scope: string;
}

export function catalogRuleTeamIris(teamIds: string[]): string[] {
  return teamIds.map(id => '/organisation_profile_teams/' + id);
}

// A connection carries at most two rules: one that accepts automatically and one that does not.
// Everything a rule decides is in here — it deliberately sets no theme and no subsidies, because
// those are decisions about a single course, not about who may see a catalogue.
export function newCatalogRuleBody(connectionId: string, teamIds: string[], autoAcceptRequest: boolean) {
  return {
    branchConnection: '/organisation_branch_connections/' + connectionId,
    teams: catalogRuleTeamIris(teamIds),
    autoAcceptRequest,
    grantsPossible: false,
    theme: null,
  };
}

export function findCatalogRule(rules: BranchConnectionCatalogRule[], autoAcceptRequest: boolean): BranchConnectionCatalogRule | null {
  return rules.find(r => r.autoAcceptRequest === autoAcceptRequest) ?? null;
}

// The API reports a bad team or theme as a 422 carrying its own translation key.
export function catalogRuleErrorKey(error: any): string {
  const violations: any[] = error?.error?.violations ?? [];
  const candidates = [...violations.map(v => v?.message), ...violations.map(v => v?.code), error?.error?.detail];
  const key = candidates.find(c => typeof c === 'string' && c.startsWith('catalog_rule.'));
  return key ? 'error.' + key : 'error.general';
}

// The API sends IRIs, but an embedded resource works just as well.
function toId(value: any): string {
  return typeof value === 'string' ? (value.split('/').pop() ?? '') : (value?.id ?? '');
}

// A PHP array whose keys are no longer sequential — after a team was removed from the rule, say —
// serialises as a JSON object ({"2": {…}}) rather than as a list, so take the values either way.
function toList(value: any): any[] {
  if (Array.isArray(value)) { return value; }
  return value && typeof value === 'object' ? Object.values(value) : [];
}

@Injectable({
  providedIn: 'root'
})

export class BranchConnectionCatalogRuleAdapter implements Adapter<BranchConnectionCatalogRule> {
  adapt(item: any): BranchConnectionCatalogRule {
    return {
      id: item.id,
      connectionId: toId(item.branchConnection),
      teams: toList(item.teams).map((t: any) => ({ id: toId(t), name: t?.name ?? '' } as CatalogRuleTeam)),
      autoAcceptRequest: item.autoAcceptRequest ?? false,
      grantsPossible: item.grantsPossible ?? true,
      themeId: toId(item.theme),
      active: item.active ?? true,
      scope: item.scope ?? 'open_courses'
    }
  }
}
